import React, { useContext, useEffect, useState } from 'react'
import globalStyles from '../../../../Styles/ComponentStyles/global.module.scss';
import styles from '../../../../Styles/ComponentStyles/ProfileStyle/ProfileNewIUL.module.scss'
import { Autocomplete, FormControlLabel, Grid, IconButton, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import GlobalNavbar from '../../../../GlobalComponent/GlobalNavbar/GlobalNavbar'
import { useLocation, useNavigate } from 'react-router-dom'
import Astrict from '../../../../GlobalComponent/Astrict/Astrict';
import GlobalTextfield from '../../../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import GlobalButton from '../../../../Utils/GlobalUtilsComponents/GlobalButton';
import IconsColor from '../../../../Utils/IconsColor';
import CancelIcon from '@mui/icons-material/Cancel';
import { UserContext, UserContextPayload } from '../../../../Context/UserDetailContext';
import { ProfileContext, ProfileContextPayload } from '../../../../Context/ProfileDetailsContext';
import { useSnackbar } from '../../../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import agent from '../../../../service/api';
import ExtractDataDiallog from '../../../../GlobalComponent/ExtractDataDialog/ExtractDataDiallog';
import GlobalPopUp from '../../../../GlobalComponent/GlobalPopup/GlobalPopup';
import { formatWithThousandSeparator } from '../../../../Utils/HelperFunctions';
import PdfExtractionHandler from '../../../../GlobalComponent/AIPdfExtractor/PdfExtractionHandler';

interface FormDataState {
    universalInput: {
        firstName: string;
        currentAge: string;
        ageAtRetirement: string;
        Contact: string;
        email: string;
    };
    note: {
        note: string;
    };
    IUL: {
        user_iul_annual_premium_data: Array<{
            rowId: string;
            annualPremium: string;
            duration: string;
        }>;
        lumpSumPremium: string;
        rateOfReturn: string;
        annualSpendableIncome: string;
        incomeDuration: string;
        incomeTaxRate: string;
    };
    typicalPlan: {
        user_tpi_contribution_data: Array<{
            rowId: string;
            annualContribution: string;
            duration: string;
        }>;
        user_tpi_monthly_flag: boolean;
        user_tpi_annually_flag: boolean;
        currentAccountBalance: string;
        percentFees: string;
        employerMatch: string;
        rateOfReturnDuringRetirement: string;
        rateOfReturnDuringWorkingYears: string;
        incomeTaxRateBeforeRetirement: string;
        incomeTaxRateDuringRetirement: string;
    };
    // In form state the illustration fields hold the raw text-area value (string).
    // They are split into a string[] only when building the API payload.
    IULFeeDetails: {
        IULillustration: string
    };
    IULAccountValueDetail: {
        IULillustration: string
    };
    IULDeathBenifits: {
        IULillustration: string
    };
    AssignProfile: {
        agentId: number | null;
        agentName: string;
    };
    sample_profile?: boolean;
    [key: string]: any;
}

type IllustrationField = 'IULFeeDetails' | 'IULAccountValueDetail' | 'IULDeathBenifits';

const ALLOWED_MESSAGE_ORIGIN = 'https://www.retirementbrain.ai:8081';
const MAX_PARSE_ROWS = 5;

// Fields whose values are formatted with thousand separators.
const THOUSAND_SEPARATOR_FIELDS = new Set([
    'lumpSumPremium', 'annualSpendableIncome', 'incomeDuration',
    'Contribution', 'currentAccountBalance', 'employerMatch'
]);

// Fields constrained to the 0-100 percentage range.
const PERCENTAGE_FIELDS = new Set([
    'rateOfReturn', 'incomeTaxRate', 'percentFees',
    'rateOfReturnDuringRetirement', 'rateOfReturnDuringWorkingYears',
    'incomeTaxRateBeforeRetirement', 'incomeTaxRateDuringRetirement'
]);

const REQUIRED_IUL_FIELDS: ReadonlyArray<{ key: string; message: string }> = [
    { key: 'lumpSumPremium', message: 'Lump sum premium is required' },
    { key: 'rateOfReturn', message: 'Rate of return is required' },
    { key: 'annualSpendableIncome', message: 'Annual spendable income is required' },
    { key: 'incomeDuration', message: 'Income duration is required' },
    { key: 'incomeTaxRate', message: 'Income tax rate is required' },
];

const REQUIRED_TPI_FIELDS: ReadonlyArray<{ key: string; message: string }> = [
    { key: 'currentAccountBalance', message: 'Current account balance is required' },
    { key: 'percentFees', message: 'Percent fees is required' },
    { key: 'employerMatch', message: 'Employer match is required' },
    { key: 'rateOfReturnDuringRetirement', message: 'Rate of return during retirement is required' },
    { key: 'rateOfReturnDuringWorkingYears', message: 'Rate of return during working years is required' },
    { key: 'incomeTaxRateBeforeRetirement', message: 'Income tax rate before retirement is required' },
    { key: 'incomeTaxRateDuringRetirement', message: 'Income tax rate during retirement is required' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;

// Field-level validators that, when satisfied, clear an existing error.
const FIELD_CLEAR_VALIDATORS: Record<string, (value: any) => boolean> = {
    'universalInput.firstName': (v) => /^[A-Za-z0-9 ]{1,30}$/.test(v?.trim()),
    'universalInput.currentAge': (v) => v >= 0 && v <= 120,
    'universalInput.ageAtRetirement': (v) => v >= 0 && v <= 120,
    'universalInput.Contact': (v) => /^\d{10}$/.test(v),
    'universalInput.email': (v) => EMAIL_REGEX.test(v),
};

// --- Pure helpers (module scope) -------------------------------------------

const isBlank = (value: any): boolean => !value && value !== 0;

const formatNumberWithCommas = (rawValue: string): string => {
    const stripped = rawValue.replaceAll(',', '');
    if (stripped.trim() !== '' && !Number.isNaN(Number(stripped))) {
        return Number(stripped).toLocaleString();
    }
    return stripped;
};

const isPercentOutOfRange = (value: string): boolean => Number(value) < 0 || Number(value) > 100;

function parseText(str: string): string {
    const rows: string[][] = str.split('+@@+').map((row) => row.split('?!#!?'));

    let count = rows.length - 1;
    if (count > MAX_PARSE_ROWS) {
        count = MAX_PARSE_ROWS;
    }

    const columns: string[] = [];
    for (let i = 0; i < count; i++) {
        for (let j = 0; j < rows[i].length; j++) {
            columns[j] = columns[j] === undefined
                ? rows[i][j]
                : `${columns[j]}       ${rows[i][j]}`;
        }
    }

    return columns
        .join(' \n ')
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll('{', '')
        .replaceAll('}', '')
        .replaceAll(']', '')
        .replaceAll('[', '');
}

// VALIDATE & CLEAN illustration text into an array of numeric strings.
const cleanIULData = (
    data: string | undefined | null
): { cleanedData: string[]; hasError: boolean; errors?: string[] } => {
    if (!data || typeof data !== 'string' || data.trim().length === 0) {
        return { cleanedData: [], hasError: true, errors: ['No data provided'] };
    }

    const cleanedLines = data
        .replaceAll('\r\n', '\n')          // normalize CRLF
        .replaceAll(',', '')               // remove thousands separators so 36,000 -> 36000
        .replaceAll(/[^\d.\s]/g, ' ')      // strip everything except digits, dot, whitespace
        .split('\n')
        .map((l) => l.trim().replaceAll(/\s+/g, ' ')) // trim & collapse whitespace
        .filter((l) => l.length > 0);

    const numRe = /^\d+(?:\.\d+)?$/;
    const errors: string[] = [];

    cleanedLines.forEach((line, i) => {
        const tokens = line.split(' ');
        const bad = tokens.filter((t) => !numRe.test(t));
        if (bad.length > 0) {
            errors.push(`Line ${i + 1}: invalid values [${bad.join(', ')}]`);
        }
    });

    return {
        cleanedData: cleanedLines,
        hasError: cleanedLines.length === 0 || errors.length > 0,
        errors: errors.length ? errors : undefined,
    };
};

// --- Validation (decomposed to keep cognitive complexity low) --------------

const validateUniversalInput = (
    universalInput: any,
    requireContactAndEmail: boolean
): { errors: any; isValid: boolean } => {
    const errors: any = {};
    let isValid = true;

    if (!universalInput?.firstName?.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
    } else if (!/^[A-Za-z0-9 ]{1,30}$/.test(universalInput.firstName)) {
        errors.firstName = 'Please enter a valid firstname';
        isValid = false;
    }

    if (!universalInput?.currentAge) {
        errors.currentAge = 'Current age is required';
        isValid = false;
    } else if (universalInput.currentAge < 0 || universalInput.currentAge > 120) {
        errors.currentAge = 'Please enter a valid age between 0 and 120';
        isValid = false;
    }

    if (!universalInput?.ageAtRetirement) {
        errors.ageAtRetirement = 'Retirement age is required';
        isValid = false;
    } else if (universalInput.ageAtRetirement <= universalInput.currentAge) {
        errors.ageAtRetirement = 'Retirement age must be greater than current age';
        isValid = false;
    }

    if (requireContactAndEmail) {
        if (!universalInput?.Contact?.trim()) {
            errors.Contact = 'Contact number is required';
            isValid = false;
        } else if (!/^\d{10}$/.test(universalInput.Contact)) {
            errors.Contact = 'Please enter a valid 10-digit contact number';
            isValid = false;
        }

        if (!universalInput?.email?.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!EMAIL_REGEX.test(universalInput.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    return { errors, isValid };
};

const validateRowData = (
    rows: any[] | undefined,
    valueField: string,
    emptyMessage: string
): { errors: any; isValid: boolean } => {
    if (!rows || rows.length === 0) {
        return { errors: emptyMessage, isValid: false };
    }

    const rowErrors: any[] = [];
    let isValid = true;

    rows.forEach((item, index) => {
        const itemErrors: any = {};
        if (!item[valueField]?.trim()) {
            itemErrors[valueField] = 'This field is required';
            isValid = false;
        }
        if (!item.duration?.trim()) {
            itemErrors.duration = 'This field is required';
            isValid = false;
        }
        if (Object.keys(itemErrors).length > 0) {
            rowErrors[index] = itemErrors;
        }
    });

    return { errors: rowErrors.length > 0 ? rowErrors : undefined, isValid };
};

const validateRequiredFields = (
    section: any,
    requiredFields: ReadonlyArray<{ key: string; message: string }>
): { errors: any; isValid: boolean } => {
    const errors: any = {};
    let isValid = true;

    requiredFields.forEach(({ key, message }) => {
        if (isBlank(section?.[key])) {
            errors[key] = message;
            isValid = false;
        }
    });

    return { errors, isValid };
};

const validateIULSection = (iul: any): { errors: any; isValid: boolean } => {
    const premium = validateRowData(
        iul?.user_iul_annual_premium_data,
        'annualPremium',
        'At least one input for annual premium data is required'
    );
    const required = validateRequiredFields(iul, REQUIRED_IUL_FIELDS);

    const errors: any = { ...required.errors };
    if (premium.errors) {
        errors.user_iul_annual_premium_data = premium.errors;
    }

    return { errors, isValid: premium.isValid && required.isValid };
};

const validateTypicalPlanSection = (typicalPlan: any): { errors: any; isValid: boolean } => {
    const contribution = validateRowData(
        typicalPlan?.user_tpi_contribution_data,
        'annualContribution',
        'At least one input for contribution data is required'
    );
    const required = validateRequiredFields(typicalPlan, REQUIRED_TPI_FIELDS);

    const errors: any = { ...required.errors };
    if (contribution.errors) {
        errors.user_tpi_contribution_data = contribution.errors;
    }

    return { errors, isValid: contribution.isValid && required.isValid };
};

const validateIllustration = (
    raw: any,
    requiredMessage: string,
    numericMessage: string
): { error?: string; isValid: boolean } => {
    const result = cleanIULData(raw);
    if (!result.hasError) {
        return { isValid: true };
    }
    return {
        error: result.cleanedData.length === 0 ? requiredMessage : numericMessage,
        isValid: false,
    };
};

const validateProfileForm = (
    formData: any,
    flagSaveAsSample: boolean
): { isValid: boolean; errors: any } => {
    const errors: any = {};
    let isValid = true;

    const requireContactAndEmail = !formData?.sample_profile || flagSaveAsSample;
    const universal = validateUniversalInput(formData?.universalInput, requireContactAndEmail);
    errors.universalInput = universal.errors;
    isValid = isValid && universal.isValid;

    const iul = validateIULSection(formData?.IUL);
    errors.IUL = iul.errors;
    isValid = isValid && iul.isValid;

    const typicalPlan = validateTypicalPlanSection(formData?.typicalPlan);
    errors.typicalPlan = typicalPlan.errors;
    isValid = isValid && typicalPlan.isValid;

    const fee = validateIllustration(
        formData?.IULFeeDetails?.IULillustration,
        'IUL fee details illustration is required',
        'IUL fee details must contain only numbers'
    );
    if (!fee.isValid) {
        errors.IULFeeDetails = { IULillustration: fee.error };
        isValid = false;
    }

    const accountValue = validateIllustration(
        formData?.IULAccountValueDetail?.IULillustration,
        'IUL account value detail illustration is required',
        'IUL account value details must contain only numbers'
    );
    if (!accountValue.isValid) {
        errors.IULAccountValueDetail = { IULillustration: accountValue.error };
        isValid = false;
    }

    const deathBenefit = validateIllustration(
        formData?.IULDeathBenifits?.IULillustration,
        'IUL death benefits illustration is required',
        'IUL death benefits must contain only numbers'
    );
    if (!deathBenefit.isValid) {
        errors.IULDeathBenifits = { IULillustration: deathBenefit.error };
        isValid = false;
    }

    return { isValid, errors };
};

const transformDataForCreate = (formData: any, flagSaveAsSample: boolean) => ({
    user_name: formData?.universalInput?.firstName,
    user_current_age: formData?.universalInput?.currentAge,
    user_retirement_age: formData?.universalInput?.ageAtRetirement,
    user_contact: formData?.universalInput?.Contact,
    user_email: formData?.universalInput?.email,
    user_notes: formData?.note?.note,

    user_iul_annual_premium_data: formData?.IUL?.user_iul_annual_premium_data?.map((item: any) => ({
        annual_premium: item?.annualPremium,
        duration: item?.duration
    })),

    user_tpi_contribution_data: formData?.typicalPlan?.user_tpi_contribution_data?.map((item: any) => ({
        total_contribution: item?.annualContribution,
        duration: item?.duration
    })),

    user_iul_lump_sum_premium: formData?.IUL?.lumpSumPremium,
    user_iul_rate_of_return: formData?.IUL?.rateOfReturn,
    user_iul_annual_spendable_income: formData?.IUL?.annualSpendableIncome,
    user_iul_income_duration: formData?.IUL?.incomeDuration,
    user_iul_it_rate_br: formData?.IUL?.incomeTaxRate,

    user_tpi_monthly_flag: formData?.typicalPlan?.user_tpi_monthly_flag,
    user_tpi_annually_flag: formData?.typicalPlan?.user_tpi_annually_flag,
    user_tpi_curr_account_balance: formData?.typicalPlan?.currentAccountBalance,
    user_tpi_percent_fees: formData?.typicalPlan?.percentFees,
    user_tpi_employer_match: formData?.typicalPlan?.employerMatch,
    user_tpi_rate_of_return_dr: formData?.typicalPlan?.rateOfReturnDuringRetirement,
    user_tpi_rate_of_return_br: formData?.typicalPlan?.rateOfReturnDuringWorkingYears,
    user_tpi_it_rate_br: formData?.typicalPlan?.incomeTaxRateBeforeRetirement,
    user_tpi_it_rate_dr: formData?.typicalPlan?.incomeTaxRateDuringRetirement,

    user_iul_fee_detail: cleanIULData(formData?.IULFeeDetails?.IULillustration).cleanedData,
    user_iul_account_value_detail: cleanIULData(formData?.IULAccountValueDetail?.IULillustration).cleanedData,
    user_iul_death_benefit: cleanIULData(formData?.IULDeathBenifits?.IULillustration).cleanedData,

    user_iul_assigned_agent_id: formData?.AssignProfile?.agentId ? [formData?.AssignProfile?.agentId] : [],

    sample_profile_flag: !flagSaveAsSample && Boolean(formData?.sample_profile),
    user_profile_type_id: 1
});

// --- Shared key-press guards ----------------------------------------------

const handleIntegerKeyPress = (e: any) => {
    if (!/\d$/.test(e.key)) {
        e.preventDefault();
    }
};

const handleDecimalKeyPress = (e: any) => {
    const value = e.target.value;
    if (!/^[\d.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
        e.preventDefault();
    }
};

const handleIllustrationKeyPress = (e: any) => {
    if (e.key === 'Enter' || e.key === ' ' || /^[\d.]$/.test(e.key)) {
        return;
    }
    e.preventDefault();
};

const isDevEnvironment = (): boolean => {
    const origin = globalThis?.location?.origin ?? '';
    return origin.includes('localhost') || origin.includes('dev');
};

// Stable, session-unique ids so list rows don't rely on array index for React keys.
let rowSequence = 0;
const generateRowId = (): string => `row-${++rowSequence}`;

const createInitialFormData = (): FormDataState => ({
    universalInput: {
        firstName: '',
        currentAge: '',
        ageAtRetirement: '',
        Contact: '',
        email: ''
    },
    note: {
        note: ''
    },
    IUL: {
        user_iul_annual_premium_data: [{
            rowId: generateRowId(),
            annualPremium: '',
            duration: ''
        }],
        lumpSumPremium: '',
        rateOfReturn: '',
        annualSpendableIncome: '',
        incomeDuration: '',
        incomeTaxRate: ''
    },
    typicalPlan: {
        user_tpi_contribution_data: [{
            rowId: generateRowId(),
            annualContribution: '',
            duration: ''
        }],
        user_tpi_monthly_flag: true,
        user_tpi_annually_flag: false,
        currentAccountBalance: '',
        percentFees: '',
        employerMatch: '',
        rateOfReturnDuringRetirement: '',
        rateOfReturnDuringWorkingYears: '',
        incomeTaxRateBeforeRetirement: '',
        incomeTaxRateDuringRetirement: ''
    },
    IULFeeDetails: {
        IULillustration: ''
    },
    IULAccountValueDetail: {
        IULillustration: ''
    },
    IULDeathBenifits: {
        IULillustration: ''
    },
    AssignProfile: {
        agentId: null,
        agentName: ''
    }
});

const ProfileNewIUL = () => {

    const {
        setFlagLoader,
        userDetail
    } = useContext(UserContext) as UserContextPayload

    const {
        createNewUserProfile,
        finalData,
        setFinalData,
        getparticularUserProfileDetail,
        updateUserProfileAPI,
        setProfileName,
        setFlagImportBtnClicked,
        extractedTextData,
        setExtractedTextData,
        flagOpenExtractDataDialog,
        setFlagOpenExtractDataDialog
    } = useContext(ProfileContext) as ProfileContextPayload

    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const location = useLocation();

    const queryParams = new URLSearchParams(location?.search);
    const userId = queryParams.get('user_id');
    const isEditMode = Boolean(userId);
    const userRole = userDetail?.user_data?.user_role_type?.toLowerCase();

    const handleCancel = () => {
        navigate(-1)
    }

    const [agentList, setAgentList] = useState<any>([])
    const [finalFormData, setFinalFormData] = useState<FormDataState>(createInitialFormData());
    const [formErrors, setFormErrors] = useState<any>({});
    const [textAreaFieldName, setTextAreaFieldName] = useState<any>('')
    const [flagSampleProfile, setFlagSampleProfile] = useState(false)
    const [flagSaveAsSampleProfile, setFlagSaveAsSampleProfile] = useState(false)

    // INPUT HANDLER for multiline illustration fields (clean only).
    const handleMultilineInputChange = (field: IllustrationField, value: string) => {
        const cleanedText = value?.split('\n')?.join('\n');

        setFinalFormData((prev: any) => ({
            ...prev,
            [field]: {
                ...prev[field],
                IULillustration: cleanedText,
            },
        }));

        setFormErrors((prevErrors: any) => {
            if (cleanedText.trim().length === 0 || !prevErrors[field]?.IULillustration) {
                return prevErrors;
            }
            const updatedErrors = { ...prevErrors, [field]: { ...prevErrors[field] } };
            delete updatedErrors[field].IULillustration;
            return updatedErrors;
        });
    };

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== ALLOWED_MESSAGE_ORIGIN) {
                return;
            }
            const val = parseText(event.data).replaceAll('*', '');
            if (textAreaFieldName) {
                handleMultilineInputChange(textAreaFieldName, val);
            }
            setFlagOpenExtractDataDialog(false);
        };

        globalThis.addEventListener('message', handleMessage);
        return () => globalThis.removeEventListener('message', handleMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textAreaFieldName]);

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === 'monthly') {
            handleInputChange('typicalPlan', 'user_tpi_monthly_flag', true)
            handleInputChange('typicalPlan', 'user_tpi_annually_flag', false)
        } else if (value === 'annually') {
            handleInputChange('typicalPlan', 'user_tpi_monthly_flag', false)
            handleInputChange('typicalPlan', 'user_tpi_annually_flag', true)
        }
        setFinalData({ ...finalData });
    };

    const handleAgentChange = (_event: React.SyntheticEvent, newValue: any) => {
        handleInputChange('AssignProfile', 'agentId', newValue?.value);
        handleInputChange('AssignProfile', 'agentName', newValue?.label);
    };

    const handleAddIncomeSource = (type: 'IUL' | 'typicalPlan'): void => {
        if (type === 'IUL') {
            setFinalFormData((prev: any) => ({
                ...prev,
                IUL: {
                    ...prev.IUL,
                    user_iul_annual_premium_data: [
                        ...prev.IUL.user_iul_annual_premium_data,
                        { rowId: generateRowId(), annualPremium: '', duration: '' }
                    ]
                }
            }));
        } else {
            setFinalFormData((prev: any) => ({
                ...prev,
                typicalPlan: {
                    ...prev.typicalPlan,
                    user_tpi_contribution_data: [
                        ...prev.typicalPlan.user_tpi_contribution_data,
                        { rowId: generateRowId(), annualContribution: '', duration: '' }
                    ]
                }
            }));
        }
    };

    const handleRemoveSource = (type: 'IUL' | 'typicalPlan', index: number): void => {
        if (type === 'IUL') {
            if (finalFormData.IUL.user_iul_annual_premium_data.length > 1) {
                setFinalFormData((prev: any) => ({
                    ...prev,
                    IUL: {
                        ...prev.IUL,
                        user_iul_annual_premium_data: prev.IUL.user_iul_annual_premium_data.filter((_: any, i: any) => i !== index)
                    }
                }));
            }
        } else if (finalFormData.typicalPlan.user_tpi_contribution_data.length > 1) {
            setFinalFormData((prev: any) => ({
                ...prev,
                typicalPlan: {
                    ...prev.typicalPlan,
                    user_tpi_contribution_data: prev.typicalPlan.user_tpi_contribution_data.filter((_: any, i: any) => i !== index)
                }
            }));
        }
    };

    const clearFieldError = (prevErrors: any, section: string, field: string, value: any) => {
        const updated = { ...prevErrors };
        const sectionErrors = { ...updated[section] };
        const validator = FIELD_CLEAR_VALIDATORS[`${section}.${field}`];

        if (validator) {
            if (validator(value)) {
                delete sectionErrors[field];
            }
        } else if ((section === 'IUL' || section === 'typicalPlan') && value?.trim?.()) {
            delete sectionErrors[field];
        }

        updated[section] = sectionErrors;
        return updated;
    };

    const handleInputChange = (section: keyof FormDataState, field: string, value: any) => {
        let nextValue = value;

        if (THOUSAND_SEPARATOR_FIELDS.has(field)) {
            nextValue = formatNumberWithCommas(nextValue);
        }

        if (PERCENTAGE_FIELDS.has(field) && isPercentOutOfRange(nextValue)) {
            return;
        }

        setFinalFormData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: nextValue
            }
        }));

        setFormErrors((prevErrors: any) => clearFieldError(prevErrors, section as string, field, nextValue));
    };

    const clearArrayFieldError = (
        prevErrors: any,
        section: string,
        arrayField: string,
        index: number,
        field: string,
        value: any
    ) => {
        const updated = { ...prevErrors };
        const sectionErrors = { ...updated[section] };
        const rowErrors = [...(sectionErrors[arrayField] || [])];
        const currentRow = { ...rowErrors[index] };

        if (value?.trim()) {
            delete currentRow[field];
        }

        if (Object.keys(currentRow).length === 0) {
            rowErrors.splice(index, 1);
        } else {
            rowErrors[index] = currentRow;
        }

        sectionErrors[arrayField] = rowErrors.length > 0 ? rowErrors : undefined;
        updated[section] = sectionErrors;
        return updated;
    };

    const handleArrayInputChange = (
        section: 'IUL' | 'typicalPlan',
        arrayField: 'user_iul_annual_premium_data' | 'user_tpi_contribution_data',
        index: number,
        field: string,
        value: any
    ) => {
        let nextValue = value;
        if (field === 'annualPremium' || field === 'annualContribution') {
            nextValue = formatNumberWithCommas(nextValue);
        }

        setFinalFormData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [arrayField]: prev[section][arrayField].map((item: any, i: number) =>
                    i === index ? { ...item, [field]: nextValue } : item
                )
            }
        }));

        setFormErrors((prevErrors: any) =>
            clearArrayFieldError(prevErrors, section, arrayField, index, field, nextValue)
        );
    };

    const buildApiPayload = (isUpdate: boolean) => {
        const payload: any = transformDataForCreate(finalFormData, flagSaveAsSampleProfile);

        if (isUpdate) {
            payload.user_profile_id = userId;
        }

        if (finalFormData?.sample_profile && !flagSaveAsSampleProfile) {
            payload.user_email = '';
            payload.user_contact = '';
            payload.user_iul_assigned_agent_id = [];
        }

        return payload;
    };

    const handleSubmitResponse = (response: any, isUpdate: boolean) => {
        const actionWord = isUpdate ? 'Updated' : 'Created';

        if (response?.status) {
            const profileId = isUpdate ? userId : response?.data?.[0]?.user_profile_id;
            navigate(`/profile/iulProfileDetails/sp?user_id=${profileId}&tab=1`);
            showSnackbar(`Profile ${actionWord} Successfully!`, 'success');
            return;
        }

        if (response?.error?.code == 9038) {
            const profileId = isUpdate ? userId : response?.data?.[0]?.user_profile_id;
            navigate(`/profile/iulProfileDetails/sp?user_id=${profileId}&tab=1`);
            showSnackbar(`profile ${actionWord} successfully but the input has errors!`, 'error');
        }

        setFlagLoader(false);
        showSnackbar(response?.error?.message, 'error');
    };

    const handleProfileFormSubmit = async () => {
        try {
            const { isValid, errors } = validateProfileForm(finalFormData, flagSaveAsSampleProfile);

            if (!isValid) {
                setFormErrors(errors);
                showSnackbar('Please fill in all required fields correctly', 'error');
                return;
            }

            setFlagLoader(true);

            const isUpdate = isEditMode && !flagSaveAsSampleProfile;
            const apiPayload = buildApiPayload(isUpdate);

            const response = isUpdate
                ? await updateUserProfileAPI(apiPayload)
                : await createNewUserProfile(apiPayload);

            handleSubmitResponse(response, isUpdate);
        } catch (error) {
            console.error('Error saving profile:', error);
        } finally {
            setFlagLoader(false);
            setFlagSaveAsSampleProfile(false);
        }
    };

    useEffect(() => {
        const getAgentListData = async () => {
            try {
                setFlagLoader(true);
                const response = await agent?.getAgentDetailsAPI?.get();

                if (!response?.status) {
                    showSnackbar(response?.error?.message || 'Something went wrong!', 'error');
                    return;
                }

                const agents = response?.data?.[0]?.agents;
                if (!agents) {
                    setAgentList([]);
                    return;
                }

                const filteredAgents = agents
                    .filter((agentItem: any) =>
                        agentItem?.user_status_name?.toLowerCase() === 'active' &&
                        agentItem?.user_role_type?.toLowerCase() === 'agent')
                    .map((agentItem: any) => ({
                        label: `${agentItem.user_firstname} ${agentItem.user_lastname}`,
                        value: agentItem.user_id
                    }));
                setAgentList(filteredAgents);
            } catch (error) {
                console.error('Error fetching agent data', error);
            } finally {
                setFlagLoader(false);
            }
        };

        if (userRole === 'admin') {
            getAgentListData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        const fetchParticularUserProfileDetail = async () => {
            try {
                setFlagLoader(true);
                const response = await getparticularUserProfileDetail(userId);

                if (!response?.status || !response?.inputData?.[0]) {
                    return;
                }

                const userData = response.inputData[0];
                setProfileName(userData?.universalInput?.firstName);
                if (userData?.sample_profile) {
                    setFlagSampleProfile(true);
                }

                const formattedIUL = {
                    ...userData.IUL,
                    lumpSumPremium: formatWithThousandSeparator(userData.IUL.lumpSumPremium),
                    annualSpendableIncome: formatWithThousandSeparator(userData.IUL.annualSpendableIncome),
                    user_iul_annual_premium_data: userData.IUL.user_iul_annual_premium_data?.map((item: any) => ({
                        ...item,
                        rowId: item.rowId ?? generateRowId(),
                        annualPremium: formatWithThousandSeparator(item.annualPremium)
                    }))
                };

                const formattedTypicalPlan = {
                    ...userData.typicalPlan,
                    currentAccountBalance: formatWithThousandSeparator(userData.typicalPlan.currentAccountBalance),
                    employerMatch: formatWithThousandSeparator(userData.typicalPlan.employerMatch),
                    user_tpi_contribution_data: userData.typicalPlan.user_tpi_contribution_data?.map((item: any) => ({
                        ...item,
                        rowId: item.rowId ?? generateRowId(),
                        annualContribution: formatWithThousandSeparator(item.annualContribution)
                    }))
                };

                const accountValueText = userData.IULAccountValueDetail?.IULillustration?.join('\n') || '';
                const deathBenefitText = userData.IULDeathBenifits?.IULillustration?.join('\n') || '';
                const feeDetailText = userData.IULFeeDetails?.IULillustration?.join('\n') || '';

                const emailValue = userData?.sample_profile ? '' : userData?.universalInput?.email;
                const contactValue = userData?.sample_profile ? '' : userData?.universalInput?.Contact;

                setFinalFormData({
                    ...userData,
                    universalInput: {
                        ...userData?.universalInput,
                        email: emailValue,
                        Contact: contactValue
                    },
                    IUL: formattedIUL,
                    typicalPlan: formattedTypicalPlan,
                    IULAccountValueDetail: {
                        ...userData.IULAccountValueDetail,
                        IULillustration: accountValueText
                    },
                    IULDeathBenifits: {
                        ...userData.IULDeathBenifits,
                        IULillustration: deathBenefitText
                    },
                    IULFeeDetails: {
                        ...userData.IULFeeDetails,
                        IULillustration: feeDetailText
                    }
                });
            } catch (error) {
                console.error('Error fetching user profile:', error);
            } finally {
                setFlagLoader(false);
            }
        };

        fetchParticularUserProfileDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, isEditMode]);

    useEffect(() => {
        if (!extractedTextData) {
            return;
        }
        Object.keys(extractedTextData).forEach((field) => {
            if (extractedTextData[field]) {
                handleMultilineInputChange(field as IllustrationField, extractedTextData[field]);
                setExtractedTextData({});
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractedTextData]);

    useEffect(() => {
        if (flagSaveAsSampleProfile) {
            handleProfileFormSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagSaveAsSampleProfile]);

    const devEnvironment = isDevEnvironment();

    return (
        <Grid container>
            {flagOpenExtractDataDialog &&
                <GlobalPopUp
                    open={flagOpenExtractDataDialog}
                    onClose={() => { setFlagOpenExtractDataDialog(false); setFlagImportBtnClicked(false) }}
                    title={'Extract Data From PDF'}
                    showCloseIcon={true}
                    flagDivider
                >
                    {devEnvironment
                        ? <PdfExtractionHandler />
                        :
                        <ExtractDataDiallog
                            setFlagOpenExtractDataDialog={setFlagOpenExtractDataDialog}
                        />
                    }
                </GlobalPopUp>
            }

            <Grid item xs={12}>
                <GlobalNavbar
                    navbarBtnText={''}
                    navbarText={isEditMode ? 'Edit Profile' : 'Create New IUL Profile'}
                    flagSaveCancelBtn
                    flagHelperTooltip
                    secondaryBtnClick={handleCancel}
                    primaryBtnClick={handleProfileFormSubmit}
                    saveAsSampleProfileClick={() => { setFlagSaveAsSampleProfile(true) }}
                    iseEditMode={isEditMode}
                    flagSampleProfile={flagSampleProfile}
                    flagBorderBottom={true}
                />
            </Grid>

            <Grid item xs={12} className={`${styles.newAnnuityMainItem}`}>
                <Grid container>
                    {/* UNIVERSAL INPUT */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Universal Inputs
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Name'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'firstName', e.target.value)}
                                                    value={finalFormData?.universalInput?.firstName}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.firstName}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Age at Retirement <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age at Retirement'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={handleIntegerKeyPress}
                                                    onChange={(e) => handleInputChange('universalInput', 'ageAtRetirement', e.target.value)}
                                                    value={finalFormData?.universalInput?.ageAtRetirement}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.ageAtRetirement}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Current Age <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={handleIntegerKeyPress}
                                                    onChange={(e) => handleInputChange('universalInput', 'currentAge', e.target.value)}
                                                    value={finalFormData?.universalInput?.currentAge}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.currentAge}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Cell Phone <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalFormData?.sample_profile ? '' : 'Enter Cell Phone'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={handleIntegerKeyPress}
                                                    onChange={(e) => handleInputChange('universalInput', 'Contact', e.target.value)}
                                                    value={finalFormData?.universalInput?.Contact}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.Contact}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Email <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalFormData?.sample_profile ? '' : 'Enter Email'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'email', e.target.value)}
                                                    value={finalFormData?.universalInput?.email}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.email}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* NOTES */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Notes
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <GlobalTextfield
                                    variant="outlined"
                                    placeholder='Add Notes'
                                    size="medium"
                                    fullWidth
                                    autoComplete='off'
                                    multiline
                                    rows={2}
                                    sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                    onChange={(e) => handleInputChange('note', 'note', e.target.value)}
                                    value={finalFormData?.note?.note}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* IUL INPUTS */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Grid container sx={{ alignItems: 'center' }}>
                                    <Grid item xs={6}>
                                        <Typography
                                            component={'span'}
                                            className={`${globalStyles.globalPFlabel}`}
                                        >
                                            IUL Inputs
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} className={styles.newAnnualBtnItem}>
                                        <GlobalButton
                                            fullWidth
                                            variant='contained'
                                            value='New Annual Premium'
                                            size='medium'
                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                            startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" />}
                                            onClick={() => { handleAddIncomeSource('IUL') }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>

                                    {finalFormData?.IUL?.user_iul_annual_premium_data.map((source: any, index: number) => (
                                        <Grid item xs={12} key={source.rowId}>
                                            <Grid container sx={{ alignItems: 'center' }} className={`${globalStyles.globalPFFieldContainer}`}>
                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                    <Grid container>
                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                Annual Premium <Astrict />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <GlobalTextfield
                                                                variant="outlined"
                                                                placeholder=''
                                                                size="medium"
                                                                fullWidth
                                                                autoComplete='off'
                                                                stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                value={source?.annualPremium}
                                                                onKeyPress={handleIntegerKeyPress}
                                                                onChange={(e) => handleArrayInputChange('IUL', 'user_iul_annual_premium_data', index, 'annualPremium', e?.target?.value)}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                            <label className={globalStyles.helperTextStyle}>
                                                                {formErrors?.IUL?.user_iul_annual_premium_data?.[index]?.annualPremium}
                                                            </label>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                    <Grid container>
                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                Duration (Years) <Astrict />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <GlobalTextfield
                                                                variant="outlined"
                                                                placeholder='Enter Years'
                                                                size="medium"
                                                                fullWidth
                                                                autoComplete='off'
                                                                value={source?.duration}
                                                                onKeyPress={handleIntegerKeyPress}
                                                                onChange={(e) => handleArrayInputChange('IUL', 'user_iul_annual_premium_data', index, 'duration', e?.target?.value)}
                                                                stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            />
                                                            <label className={globalStyles.helperTextStyle}>
                                                                {formErrors?.IUL?.user_iul_annual_premium_data?.[index]?.duration}
                                                            </label>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                {finalFormData?.IUL?.user_iul_annual_premium_data?.length >= 2 &&
                                                    <Grid item sx={{ mt: '25px' }}>
                                                        <IconButton onClick={() => handleRemoveSource('IUL', index)}>
                                                            <CancelIcon sx={{ color: '#314158' }} />
                                                        </IconButton>
                                                    </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                    ))}

                                    <Grid item xs={12}>
                                        <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Lump sum Premium <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onKeyPress={handleIntegerKeyPress}
                                                            onChange={(e) => handleInputChange('IUL', 'lumpSumPremium', e?.target?.value)}
                                                            value={finalFormData?.IUL?.lumpSumPremium}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors.IUL?.lumpSumPremium}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Rate of Return (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            onChange={(e) => handleInputChange('IUL', 'rateOfReturn', e?.target?.value)}
                                                            value={finalFormData?.IUL?.rateOfReturn}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors.IUL?.rateOfReturn}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Annual Spendable Income <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onKeyPress={handleIntegerKeyPress}
                                                            onChange={(e) => handleInputChange('IUL', 'annualSpendableIncome', e?.target?.value)}
                                                            value={finalFormData?.IUL?.annualSpendableIncome}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors.IUL?.annualSpendableIncome}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Duration (Years) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder='Enter Years'
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIntegerKeyPress}
                                                            onChange={(e) => handleInputChange('IUL', 'incomeDuration', e?.target?.value)}
                                                            value={finalFormData?.IUL?.incomeDuration}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors.IUL?.incomeDuration}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Tax Rate (Before Retirement) (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            onChange={(e) => handleInputChange('IUL', 'incomeTaxRate', e?.target?.value)}
                                                            value={finalFormData?.IUL?.incomeTaxRate}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors.IUL?.incomeTaxRate}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* TYPICAL PLAN INPUTS */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Grid container sx={{ alignItems: 'center' }}>
                                    <Grid item xs={6}>
                                        <Typography
                                            component={'span'}
                                            className={`${globalStyles.globalPFlabel}`}
                                        >
                                            Typical Plan Inputs
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} className={styles.newAnnualBtnItem}>
                                        <GlobalButton
                                            fullWidth
                                            variant='contained'
                                            value='New Contribution'
                                            size='medium'
                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                            startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" />}
                                            onClick={() => { handleAddIncomeSource('typicalPlan') }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            name="radio-buttons-group"
                                            onChange={handleRadioChange}
                                            sx={{
                                                '& .MuiButtonBase-root': {
                                                    padding: '9px 4px 9px 9px !important'
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                value="monthly"
                                                control={<Radio
                                                    sx={{
                                                        color: '#3B78CE',
                                                        '&.Mui-checked': {
                                                            color: '#3B78CE',
                                                        },
                                                        '& .MuiFormControlLabel-root': { color: '#3B78CE' },
                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    '& .MuiTypography-root': {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Monthly"
                                                checked={finalFormData?.typicalPlan?.user_tpi_monthly_flag}
                                            />

                                            <FormControlLabel
                                                value="annually"
                                                control={<Radio
                                                    sx={{
                                                        color: '#3B78CE',
                                                        '&.Mui-checked': {
                                                            color: '#3B78CE',
                                                        },
                                                        '& .MuiFormControlLabel-root': { color: '#3B78CE' },
                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    '& .MuiTypography-root': {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Annually"
                                                checked={finalFormData?.typicalPlan?.user_tpi_annually_flag}
                                            />
                                        </RadioGroup>
                                    </Grid>

                                    {finalFormData?.typicalPlan?.user_tpi_contribution_data.map((source: any, index: number) => (
                                        <Grid item xs={12} key={source.rowId}>
                                            <Grid container sx={{ alignItems: 'center' }} className={`${globalStyles.globalPFFieldContainer}`}>
                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                    <Grid container>
                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                Contribution <Astrict />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <GlobalTextfield
                                                                variant="outlined"
                                                                placeholder=''
                                                                size="medium"
                                                                fullWidth
                                                                autoComplete='off'
                                                                stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                value={source?.annualContribution}
                                                                onKeyPress={handleIntegerKeyPress}
                                                                onChange={(e) => handleArrayInputChange('typicalPlan', 'user_tpi_contribution_data', index, 'annualContribution', e?.target?.value)}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                        </InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                            <label className={globalStyles.helperTextStyle}>
                                                                {formErrors?.typicalPlan?.user_tpi_contribution_data?.[index]?.annualContribution}
                                                            </label>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                    <Grid container>
                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                            <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                Duration (Years) <Astrict />
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <GlobalTextfield
                                                                variant="outlined"
                                                                placeholder='Enter Years'
                                                                size="medium"
                                                                fullWidth
                                                                autoComplete='off'
                                                                value={source?.duration}
                                                                onKeyPress={handleIntegerKeyPress}
                                                                onChange={(e) => handleArrayInputChange('typicalPlan', 'user_tpi_contribution_data', index, 'duration', e?.target?.value)}
                                                                stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            />
                                                            <label className={globalStyles.helperTextStyle}>
                                                                {formErrors?.typicalPlan?.user_tpi_contribution_data?.[index]?.duration}
                                                            </label>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                {finalFormData?.typicalPlan?.user_tpi_contribution_data?.length >= 2 &&
                                                    <Grid item sx={{ mt: '25px' }}>
                                                        <IconButton onClick={() => handleRemoveSource('typicalPlan', index)}>
                                                            <CancelIcon sx={{ color: '#314158' }} />
                                                        </IconButton>
                                                    </Grid>
                                                }
                                            </Grid>
                                        </Grid>
                                    ))}

                                    <Grid item xs={12}>
                                        <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Current Account Balance <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIntegerKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'currentAccountBalance', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.currentAccountBalance}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.currentAccountBalance}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Percent Fees (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'percentFees', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.percentFees}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.percentFees}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Employer Match (Annual $ Contribution) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIntegerKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'employerMatch', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.employerMatch}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.employerMatch}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Rate of Return during Retirement (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'rateOfReturnDuringRetirement', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.rateOfReturnDuringRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.rateOfReturnDuringRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Rate of Return during Working Years (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'rateOfReturnDuringWorkingYears', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.rateOfReturnDuringWorkingYears}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.rateOfReturnDuringWorkingYears}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Tax Rate (Before Retirement) (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'incomeTaxRateBeforeRetirement', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.incomeTaxRateBeforeRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.incomeTaxRateBeforeRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Tax Rate (During Retirement) (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleDecimalKeyPress}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange('typicalPlan', 'incomeTaxRateDuringRetirement', e?.target?.value)}
                                                            value={finalFormData?.typicalPlan?.incomeTaxRateDuringRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.incomeTaxRateDuringRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {devEnvironment
                        ?
                        <>
                            {/* IUL DETAILS SECTION */}
                            <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Grid container justifyContent={'flex-end'}>
                                            <GlobalButton
                                                fullWidth
                                                variant='contained'
                                                value='Extract From PDF'
                                                size='medium'
                                                className={`${globalStyles.globalButtonStyleV1} ${styles.extractFromPdfBtn}`}
                                                startIcon={<IconsColor iconName="extractIcon" width="20px" height="20px" />}
                                                onClick={() => { setFlagOpenExtractDataDialog(true); setTextAreaFieldName('IULFeeDetails'); setFlagImportBtnClicked(false) }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            {/* IUL Fee Detail */}
                                            <Grid item xs={12} sm={12} md={4}>
                                                <Grid container gap="15px">
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Fee Detail
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Grid container>
                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                <Typography
                                                                    component='span'
                                                                    className={`${globalStyles.globalPFTextfieldLabel}`}
                                                                >
                                                                    Data from IUL illustration <Astrict />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <GlobalTextfield
                                                                    variant="outlined"
                                                                    placeholder=''
                                                                    size="medium"
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    multiline
                                                                    rows={2.5}
                                                                    sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                    onKeyPress={handleIllustrationKeyPress}
                                                                    onChange={(e) => handleMultilineInputChange('IULFeeDetails', e.target.value)}
                                                                    value={finalFormData?.IULFeeDetails?.IULillustration || extractedTextData.IULFeeDetails}
                                                                />
                                                                <label className={globalStyles.helperTextStyle}>
                                                                    {formErrors?.IULFeeDetails?.IULillustration}
                                                                </label>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {/* IUL Account Value Detail */}
                                            <Grid item xs={12} sm={12} md={4}>
                                                <Grid container gap="15px">
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Account Value Detail
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Grid container>
                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                <Typography
                                                                    component='span'
                                                                    className={`${globalStyles.globalPFTextfieldLabel}`}
                                                                >
                                                                    Data from IUL illustration <Astrict />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <GlobalTextfield
                                                                    variant="outlined"
                                                                    placeholder=''
                                                                    size="medium"
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    multiline
                                                                    rows={2.5}
                                                                    sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                    onKeyPress={handleIllustrationKeyPress}
                                                                    onChange={(e) => handleMultilineInputChange('IULAccountValueDetail', e.target.value)}
                                                                    value={finalFormData?.IULAccountValueDetail?.IULillustration}
                                                                />
                                                                <label className={globalStyles.helperTextStyle}>
                                                                    {formErrors?.IULAccountValueDetail?.IULillustration}
                                                                </label>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {/* IUL Death Benefits */}
                                            <Grid item xs={12} sm={12} md={4}>
                                                <Grid container gap="15px">
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Death Benefits
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Grid container>
                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                <Typography
                                                                    component='span'
                                                                    className={`${globalStyles.globalPFTextfieldLabel}`}
                                                                >
                                                                    Data from IUL illustration <Astrict />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <GlobalTextfield
                                                                    variant="outlined"
                                                                    placeholder=''
                                                                    size="medium"
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    multiline
                                                                    rows={2.5}
                                                                    sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                    onKeyPress={handleIllustrationKeyPress}
                                                                    onChange={(e) => handleMultilineInputChange('IULDeathBenifits', e.target.value)}
                                                                    value={finalFormData?.IULDeathBenifits?.IULillustration}
                                                                />
                                                                <label className={globalStyles.helperTextStyle}>
                                                                    {formErrors?.IULDeathBenifits?.IULillustration}
                                                                </label>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Assign Profile (Only visible for non-agent users) */}
                            {userRole !== 'agent' && (
                                <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                                    <Grid container>
                                        <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                            <Typography
                                                component={'span'}
                                                className={`${globalStyles.globalPFlabel}`}
                                            >
                                                Assign Profile
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={9} lg={11} xl={4.5}>
                                            <Grid container>
                                                <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                    <Typography
                                                        component='span'
                                                        className={`${globalStyles.globalPFTextfieldLabel}`}
                                                    >
                                                        Agent Name
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        options={agentList}
                                                        size="medium"
                                                        className={globalStyles.globalAutoCompleteStyle}
                                                        onChange={handleAgentChange}
                                                        value={finalFormData?.AssignProfile?.agentName}
                                                        renderInput={(params) => <TextField placeholder='Select Agent' {...params} />}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                padding: '3px !important'
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </>
                        :
                        <>
                            {/* EXTRACT SECTION 1 */}
                            <Grid item xs={12}>
                                <Grid container sx={{ justifyContent: 'space-between' }}>
                                    <Grid item xs={12} sm={12} md={12} lg={5.95} xl={5.95} className={`${globalStyles.globalPFBlurWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                                <Grid container sx={{ alignItems: 'center' }}>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Fee Detail
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} className={styles.newAnnualBtnItem}>
                                                        <GlobalButton
                                                            fullWidth
                                                            variant='contained'
                                                            value='Extract From PDF'
                                                            size='medium'
                                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                                            startIcon={<IconsColor iconName="extractIcon" width="20px" height="20px" />}
                                                            onClick={() => { setFlagOpenExtractDataDialog(true); setTextAreaFieldName('IULFeeDetails') }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography
                                                            component='span'
                                                            className={`${globalStyles.globalPFTextfieldLabel}`}
                                                        >
                                                            Data from IUL illustration <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            multiline
                                                            rows={2}
                                                            sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIllustrationKeyPress}
                                                            onChange={(e) => handleMultilineInputChange('IULFeeDetails', e.target.value)}
                                                            value={finalFormData?.IULFeeDetails?.IULillustration}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.IULFeeDetails?.IULillustration}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={12} lg={5.95} xl={5.95} className={`${globalStyles.globalPFBlurWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                                <Grid container sx={{ alignItems: 'center' }}>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Account Value Detail
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} className={styles.newAnnualBtnItem}>
                                                        <GlobalButton
                                                            fullWidth
                                                            variant='contained'
                                                            value='Extract From PDF'
                                                            size='medium'
                                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                                            startIcon={<IconsColor iconName="extractIcon" width="20px" height="20px" />}
                                                            onClick={() => { setFlagOpenExtractDataDialog(true); setTextAreaFieldName('IULAccountValueDetail') }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography
                                                            component='span'
                                                            className={`${globalStyles.globalPFTextfieldLabel}`}
                                                        >
                                                            Data from IUL illustration <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            multiline
                                                            rows={2}
                                                            sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIllustrationKeyPress}
                                                            onChange={(e) => handleMultilineInputChange('IULAccountValueDetail', e.target.value)}
                                                            value={finalFormData?.IULAccountValueDetail?.IULillustration}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.IULAccountValueDetail?.IULillustration}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* EXTRACT SECTION 2 */}
                            <Grid item xs={12} marginBottom={'20px !important'}>
                                <Grid container sx={{ justifyContent: 'space-between' }}>
                                    <Grid item xs={12} sm={12} md={12} lg={5.95} xl={5.95} className={`${globalStyles.globalPFBlurWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                                <Grid container sx={{ alignItems: 'center' }}>
                                                    <Grid item xs={6}>
                                                        <Typography
                                                            component={'span'}
                                                            className={`${globalStyles.globalPFlabel}`}
                                                        >
                                                            IUL Death Benefits
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} className={styles.newAnnualBtnItem}>
                                                        <GlobalButton
                                                            fullWidth
                                                            variant='contained'
                                                            value='Extract From PDF'
                                                            size='medium'
                                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                                            startIcon={<IconsColor iconName="extractIcon" width="20px" height="20px" />}
                                                            onClick={() => { setFlagOpenExtractDataDialog(true); setTextAreaFieldName('IULDeathBenifits') }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography
                                                            component='span'
                                                            className={`${globalStyles.globalPFTextfieldLabel}`}
                                                        >
                                                            Data from IUL illustration <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            multiline
                                                            rows={2}
                                                            sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={handleIllustrationKeyPress}
                                                            onChange={(e) => handleMultilineInputChange('IULDeathBenifits', e.target.value)}
                                                            value={finalFormData?.IULDeathBenifits?.IULillustration}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.IULDeathBenifits?.IULillustration}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {userRole !== 'agent' &&
                                        <Grid item xs={12} sm={12} md={12} lg={5.95} xl={5.95} className={`${globalStyles.globalPFBlurWrapper}`}>
                                            <Grid container>
                                                <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                                    <Typography
                                                        component={'span'}
                                                        className={`${globalStyles.globalPFlabel}`}
                                                    >
                                                        Assign Profile
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                            <Typography
                                                                component='span'
                                                                className={`${globalStyles.globalPFTextfieldLabel}`}
                                                            >
                                                                Agent Name
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Autocomplete
                                                                options={agentList}
                                                                size="medium"
                                                                className={globalStyles.globalAutoCompleteStyle}
                                                                onChange={handleAgentChange}
                                                                value={finalFormData?.AssignProfile?.agentName}
                                                                renderInput={(params) => <TextField {...params} />}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </>
                    }
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ProfileNewIUL